/**
 * The owner's FTUX records in their preferences, and when the FTUX may trust and write them. Pure: it never imports
 * the Redux store, so jest tests the trap logic directly (blueprint section 4).
 *
 * Two kinds of record, each under its own top-level key (the transitions name the keys):
 *  - the owner's intro (Lumio's hello and the menu's map, once per owner);
 *  - one run per brand (the pieces, each brand on its own), so two tabs on two brands never write over each other.
 *
 * The traps this guards against:
 *  - a failed preferences GET still sets `loaded`, with {} (userPreferences.ts rejected case), and a support agent's
 *    identity swap leaves another owner's preferences loaded for a moment: neither is ever read nor written over;
 *  - the backend merges top-level keys shallowly: each record is always POSTed whole;
 *  - the global axios answers an identical POST from its 5 s cache without sending it: every record carries a fresh
 *    `updatedAt`, each one later than the last;
 *  - a record from another build: unknown ids are dropped, a newer version is left alone.
 */
import { isPrefsReliablyLoaded } from '../../../theme/isPrefsReliablyLoaded';
import { FTUX_OUTCOMES, FTUX_SCRIPT_VERSION, isBrandPieceId, type BrandPieceId, type FtuxOutcome } from '../catalog';
import type {
  FtuxIntro,
  FtuxIntroStatus,
  FtuxOutcomes,
  FtuxPatch,
  FtuxProgress,
  FtuxStatus,
  FtuxWriteValue,
} from './types';

/** The records' own format version (the script's version is `script`). */
export const FTUX_RECORD_VERSION = 1;

/** The latest moment a date holds (epoch ms), the events server's bound on `run`. */
const MAX_RUN_MS = 8_640_000_000_000_000;

/** The longest brand id a run is kept for (the events server's bound on `brandId`). */
const MAX_BRAND_ID_LENGTH = 64;

/** The owner's intro as stored. */
export type FtuxIntroRecord = {
  v: typeof FTUX_RECORD_VERSION;
  script: typeof FTUX_SCRIPT_VERSION;
  run: number;
  status: FtuxIntroStatus;
  /** ISO, fresh on every write. */
  updatedAt: string;
};

/** A brand's run as stored. */
export type FtuxRunRecord = {
  v: typeof FTUX_RECORD_VERSION;
  script: typeof FTUX_SCRIPT_VERSION;
  run: number;
  status: FtuxStatus;
  piece: BrandPieceId | null;
  outcomes: FtuxOutcomes;
  deferred: BrandPieceId[];
  /** ISO, fresh on every write. */
  updatedAt: string;
};

/** What a stored value reads as: nothing (absent, empty or malformed), a newer record, or its value. */
export type FtuxRead<T> = { kind: 'none' } | { kind: 'newer' } | { kind: 'ok'; value: T };

/** What the owner's preferences hold of the FTUX: their intro, each brand's run, and whether any is newer. */
export type FtuxRecords = {
  intro: FtuxIntro | null;
  runs: Record<string, FtuxProgress>;
  newer: boolean;
};

const INTRO_STATUSES: readonly FtuxIntroStatus[] = ['hello', 'lap', 'done'];

/**
 * A brand run's stored statuses, and the ones this build never writes there, read back as where they stand: the
 * pop-up, the piece in play and its acknowledgment (and the prototype's names for them) wait again, a wrap-up is the
 * end. The hello and the map are the intro's, never a brand's.
 */
const RUN_STATUS_READ: ReadonlyMap<string, FtuxStatus> = new Map<string, FtuxStatus>([
  ['waiting', 'waiting'],
  ['finished', 'finished'],
  ['intro', 'waiting'],
  ['pieces', 'waiting'],
  ['playing', 'waiting'],
  ['ack', 'waiting'],
  ['wrapping', 'finished'],
]);

type Bag = Record<string, unknown>;

const isBag = (value: unknown): value is Bag => typeof value === 'object' && value !== null && !Array.isArray(value);

const isOutcome = (value: unknown): value is FtuxOutcome =>
  typeof value === 'string' && (FTUX_OUTCOMES as readonly string[]).includes(value);

const isVersion = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

const isRun = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= MAX_RUN_MS;

const isIntroStatus = (value: unknown): value is FtuxIntroStatus =>
  typeof value === 'string' && (INTRO_STATUSES as readonly string[]).includes(value);

/** A brand id a run can be kept under: a non-empty string within the events server's bound. */
export const isFtuxBrandId = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_BRAND_ID_LENGTH;

/** The known pieces' known outcomes. */
const readOutcomes = (raw: unknown): FtuxOutcomes =>
  isBag(raw)
    ? Object.entries(raw).reduce<FtuxOutcomes>(
        (outcomes, [id, outcome]) =>
          isBrandPieceId(id) && isOutcome(outcome) ? { ...outcomes, [id]: outcome } : outcomes,
        {},
      )
    : {};

/** The known pieces once each, without the ones that ended or wait (a piece is in one place at a time). */
const readDeferred = (raw: unknown, outcomes: FtuxOutcomes, waiting: BrandPieceId | null): BrandPieceId[] => {
  const ids: readonly unknown[] = Array.isArray(raw) ? raw : [];
  return ids.filter(
    (id, index): id is BrandPieceId =>
      isBrandPieceId(id) && ids.indexOf(id) === index && outcomes[id] === undefined && id !== waiting,
  );
};

/** The waiting piece by id (an index, the prototype's form, or an unknown id reads as none: the run moves on). */
const readPiece = (raw: unknown, status: FtuxStatus, outcomes: FtuxOutcomes): BrandPieceId | null =>
  status === 'waiting' && isBrandPieceId(raw) && outcomes[raw] === undefined ? raw : null;

/** A stored value's versions: none (not a record), newer than this build, or this build's. */
const versionsOf = (raw: unknown): 'none' | 'newer' | 'ok' => {
  if (!isBag(raw) || !isVersion(raw.v) || !isVersion(raw.script)) return 'none';
  return raw.v > FTUX_RECORD_VERSION || raw.script > FTUX_SCRIPT_VERSION ? 'newer' : 'ok';
};

/** What the owner's intro key holds, normalized. Pure. */
export const normalizeFtuxIntro = (raw: unknown): FtuxRead<FtuxIntro> => {
  const versions = versionsOf(raw);
  if (versions !== 'ok' || !isBag(raw)) return { kind: versions === 'newer' ? 'newer' : 'none' };
  if (!isRun(raw.run) || !isIntroStatus(raw.status)) return { kind: 'none' };
  return { kind: 'ok', value: { run: raw.run, status: raw.status } };
};

/** What a brand's run key holds, normalized. Pure. */
export const normalizeFtuxRun = (raw: unknown): FtuxRead<FtuxProgress> => {
  const versions = versionsOf(raw);
  if (versions !== 'ok' || !isBag(raw)) return { kind: versions === 'newer' ? 'newer' : 'none' };
  const status = typeof raw.status === 'string' ? RUN_STATUS_READ.get(raw.status) : undefined;
  if (!isRun(raw.run) || status === undefined) return { kind: 'none' };
  const outcomes = readOutcomes(raw.outcomes);
  const piece = readPiece(raw.piece, status, outcomes);
  return {
    kind: 'ok',
    value: { run: raw.run, status, piece, outcomes, deferred: readDeferred(raw.deferred, outcomes, piece) },
  };
};

/**
 * The owner's FTUX records in their preferences bag: the intro under `introKey`, each brand's run under
 * `runKeyPrefix` + its brand id (a malformed one, or one under a malformed id, is no run). A newer record anywhere
 * makes the whole read newer: this build leaves the owner's FTUX alone. Pure.
 */
export const readFtuxRecords = (preferences: Readonly<Bag>, introKey: string, runKeyPrefix: string): FtuxRecords => {
  const intro = normalizeFtuxIntro(preferences[introKey]);
  let newer = intro.kind === 'newer';
  const runs: Record<string, FtuxProgress> = {};
  Object.entries(preferences).forEach(([key, raw]) => {
    if (!key.startsWith(runKeyPrefix)) return;
    const brandId = key.slice(runKeyPrefix.length);
    const read = normalizeFtuxRun(raw);
    if (read.kind === 'newer') newer = true;
    if (read.kind === 'ok' && isFtuxBrandId(brandId)) runs[brandId] = read.value;
  });
  return { intro: intro.kind === 'ok' ? intro.value : null, runs, newer };
};

/** The intro's record, stamped `updatedAt`. Pure. */
export const toIntroRecord = (intro: FtuxIntro, updatedAt: string): FtuxIntroRecord => ({
  v: FTUX_RECORD_VERSION,
  script: FTUX_SCRIPT_VERSION,
  run: intro.run,
  status: intro.status,
  updatedAt,
});

/** A brand run's record, stamped `updatedAt`. Pure. */
export const toRunRecord = (progress: FtuxProgress, updatedAt: string): FtuxRunRecord => ({
  v: FTUX_RECORD_VERSION,
  script: FTUX_SCRIPT_VERSION,
  run: progress.run,
  status: progress.status,
  piece: progress.piece,
  outcomes: { ...progress.outcomes },
  deferred: [...progress.deferred],
  updatedAt,
});

const storedValue = (value: FtuxWriteValue, updatedAt: string): FtuxIntroRecord | FtuxRunRecord | null => {
  if (value === null) return null;
  return value.kind === 'intro' ? toIntroRecord(value.intro, updatedAt) : toRunRecord(value.progress, updatedAt);
};

/** The body to POST for `patch`: each record stamped `updatedAt`, a tombstone (null) as it is. Pure. */
export const toStoredPatch = (
  patch: FtuxPatch,
  updatedAt: string,
): Record<string, FtuxIntroRecord | FtuxRunRecord | null> =>
  Object.fromEntries(Object.entries(patch).map(([key, value]) => [key, storedValue(value, updatedAt)]));

/** The next record's `updatedAt`: now, or a millisecond after the last one, so no two records match. Pure. */
export const freshStamp = (nowMs: number, last: string | null): string => {
  const lastMs = last === null ? Number.NaN : Date.parse(last);
  return new Date(Number.isNaN(lastMs) || nowMs > lastMs ? nowMs : lastMs + 1).toISOString();
};

/** The fields of the preferences slice that say whether its bag can be trusted. */
export type PrefsReadState = { loaded: boolean; error: string | null | undefined; loadedForUserId: string | null };

/** Whether the preferences are the signed-in owner's own, loaded without an error. */
export const prefsReliableFor = (prefs: PrefsReadState, userId: string | null): boolean =>
  userId !== null && isPrefsReliablyLoaded(prefs) && prefs.loadedForUserId === userId;

/** What decides whether the FTUX may change and write the owner's run. */
export type FtuxWriteGuard = {
  /** The layer is mounted (the flag on, nobody signed in as the owner). */
  mounted: boolean;
  /** The runs were read from the owner's preferences. */
  hydrated: boolean;
  /** A stored record is newer than this build knows. */
  newer: boolean;
  /** The owner the runs were read for. */
  sliceUserId: string | null;
  /** The owner signed in now. */
  userId: string | null;
  /** The brand the run on screen belongs to. */
  sliceBrandId: string | null;
  /** The brand displayed now. */
  brandId: string | null;
  /** A support agent is signed in as the owner. */
  impersonated: boolean;
  prefs: PrefsReadState;
};

/**
 * Whether the run may start, move or jump, and its records be written: never on preferences that cannot be trusted,
 * and never while the run in the tab belongs to another brand than the one displayed (the moment of a brand switch).
 */
export const mayWriteFtux = (guard: FtuxWriteGuard): boolean =>
  guard.mounted &&
  guard.hydrated &&
  !guard.newer &&
  !guard.impersonated &&
  guard.sliceUserId === guard.userId &&
  guard.sliceBrandId !== null &&
  guard.sliceBrandId === guard.brandId &&
  prefsReliableFor(guard.prefs, guard.userId);
